import React, { useEffect, useState } from "react";
import AuctionSummaryLoginSvg from "../../../../icons/AuctionSummaryLoginSvg";
import UpcomingActionLoginSvg from "../../../../icons/UpcomingActionLoginSvg";
import BidderLoginForm from "./BidderLoginForm";
import BidderOTPInput from "./BidderOTPInput";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import { Loader } from "app";
import { BetaForm as useBetaForm } from "@reusejs/react";

export default function Login() {
  const router = useRouter();
  const [inputModalOpen, setInputModalOpen] = useState(true);
  const accessToken = useSelector((state: any) => state.bidderAuth.accessToken);
  const [loading, setLoading] = useState(!!accessToken);
  const [showLogoutMsg, setShowLogoutMsg] = useState(false);

  useEffect(() => {
    if (router.query.sessionExpired === "true") {
      setShowLogoutMsg(true);
      router.replace("/login", undefined, { shallow: true });
      const timer = setTimeout(() => setShowLogoutMsg(false), 15000);
      return () => clearTimeout(timer);
    }
  }, [router.query.sessionExpired]);

  useEffect(() => {
    if (!router.isReady) return;

    const redirectIfLoggedIn = async () => {
      // Only redirect if user is actually logged in and not already on login page
      if (
        accessToken &&
        accessToken.isLoggedIn &&
        router.pathname === "/login"
      ) {
        // Add a small delay to prevent rapid redirects
        await new Promise((resolve) => setTimeout(resolve, 200));

        const redirectUri =
          (router.query.redirect_uri as string) ||
          (typeof window !== "undefined"
            ? localStorage.getItem("bidder_redirect_uri")
            : null);

        if (redirectUri) {
          router.replace(redirectUri);
        } else {
          router.replace("/").catch((err) => {
            console.error("Redirect failed:", err);
          });
        }
      } else {
        setLoading(false);
      }
    };

    redirectIfLoggedIn();
  }, [accessToken, router?.pathname, router.isReady]);

  const form = useBetaForm({
    user_identity: "",
  });
  const form_constraints = {
    user_identity: {
      presence: {
        allowEmpty: false,
        message: "^Please enter a valid email or mobile number.",
      },
    },
  };

  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, []);

  if (loading) {
    return (
      <main className="justify-center max-w-7xl mx-auto flex flex-col items-center min-h-screen">
        <Loader height="h-16" width="w-16" color="text-brand-orange" />
      </main>
    );
  }

  return (
    <div className="flex flex-col justify-center items-center dark:bg-[#152e4d] px-customPaddingMobile lg:px-customPadding w-full 2xl:w-[1340px] 2xl:mx-auto 2xl:px-0 relative">
      {showLogoutMsg && (
        <div className="sticky lg:absolute top-16 lg:top-0 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg z-50 flex items-center max-w-xl text-xs md:text-sm font-openSans">
          <span>
            You have been logged out because of session expiry. Please log in
            again.
          </span>
          <button
            className="ml-4 text-red-700 font-bold"
            onClick={() => setShowLogoutMsg(false)}
          >
            ×
          </button>
        </div>
      )}
      <div className="w-full flex flex-col justify-center items-center lg:justify-start lg:flex-row px-0 lg:px-4 mt-4 lg:mt-8 gap-3">
        <div className="w-full md:w-1/2 h-full text-center md:text-left">
          <p className="font-normal text-login-color font-primary uppercase text-4xl mid:text-[53px] leading-normal hidden lg:block">
            Simply manage your
          </p>
          <p className="uppercase font-primary font-normal text-4xl mid:text-[53px] text-login-color hidden lg:block">
            auctions
          </p>
          <p className="uppercase font-primary font-normal text-2xl lg:hidden text-login-color text-left">
            simply Manage your auctions
          </p>
          <div className="flex justify-center lg:justify-start ">
            <div className="relative mt-7 w-[320px] h-[240px] mid:h-[330px] mid:w-full flex justify-center mid:block ">
              <UpcomingActionLoginSvg className="shadow-2xl ml-0 mr-10 mid:mr-0 mid:ml-10 h-[160px] mid:h-auto" />
              <div className="absolute right-0 mid:right-20 top-24 mid:top-32">
                <AuctionSummaryLoginSvg className="shadow-2xl h-[105px] mid:h-[210px] w-[100px] mid:w-[200px]" />
              </div>
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/2 bg-[#F8F8F8] px-4 py-8 lg:py-20 lg:px-16 clip-path-bottomRightFooterMobile lg:clip-path-bottomRightPropertyCountCard">
          {inputModalOpen && (
            <BidderLoginForm
              setInputModalOpen={setInputModalOpen}
              inputModalOpen={inputModalOpen}
              form={form}
            />
          )}
          {!inputModalOpen && (
            <BidderOTPInput
              setInputModalOpen={setInputModalOpen}
              inputModalOpen={inputModalOpen}
              formData={form}
            />
          )}
        </div>
      </div>
    </div>
  );
}
